import { OETrans } from '@components/translation/OETrans';
import { ConfirmSolid, Eye, EyeSlash, RejectSolid } from '@icons';
import { changePassword } from '@lib/api';
import { useState } from 'react';
import { Button, Col, Form, InputGroup, Modal, Row } from 'react-bootstrap';

const passwordRegex = /^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{8,}$/; // eslint-disable-line

export default function NewPasswordForm({ profileFromApi, callback = undefined }) {
	const [profile, setProfile] = useState(profileFromApi);
	const [isLoading, setIsLoading] = useState(false);
	const [isSaved, setIsSaved] = useState(false);
	const [isError, setIsError] = useState(false);

	const [fieldsError, setFieldsError] = useState({});

	const [serverErrorFields, setServerErrorFields] = useState({});

	const handleChange = (e) => {
		const { name, value } = e.target;
		setProfile({
			...profile,
			[name]: value,
		});

		// Remove error if field is not empty
		if (fieldsError[name] && value.trim()) {
			const newErrors = { ...fieldsError };
			delete newErrors[name];
			setFieldsError(newErrors);
		}
		if (serverErrorFields[name] && value.trim()) {
			const newErrors = { ...fieldsError };
			delete newErrors[name];
			setServerErrorFields(newErrors);
		}
	};

	const validation = (fields) => {
		const newErrors = { ...fieldsError };
		fields.forEach((field) => {
			if (!profile[field] || profile[field].trim() === '') {
				newErrors[field] = true;
			}
		});

		// Check if password is valid
		if (profile.password && !profile.password.match(passwordRegex) && fields.includes('password')) {
			newErrors['password'] = true;
		}

		// Check if passwords match
		if (profile.password !== profile.passwordConfirmation && fields.includes('password')) {
			newErrors['passwordConfirmation'] = true;
		}
		setFieldsError(newErrors);
		return newErrors;
	};
	const handleSavePassword = () => {
		const fields = ['oldPassword', 'password', 'passwordConfirmation'];
		const newErrors = validation(fields);
		if (Object.keys(newErrors).length === 0) {
			setIsLoading(true);
			changePassword(profile)
				.then((response) => {
					if (response.success) {
						setIsSaved(true);
					} else {
						if (response.message === 'Validation errors') {
							setServerErrorFields(response.data);
						} else setIsError(true);
					}
					// Clear password fields
					setProfile({
						...profile,
						oldPassword: '',
						password: '',
						passwordConfirmation: '',
					});
					setValidConditions({
						length: false,
						uppercase: false,
						number: false,
						specialChar: false,
					});
					setIsLoading(false);
				})
				.catch((error) => {
					setIsError(true);
					setIsLoading(false);
				});
		}
	};

	const validationMap = {
		length: {
			isValid: (value) => value.length >= 10,
			label: 'Contenere almeno 10 caratteri',
		},
		uppercase: {
			isValid: (value) => /[A-Z]/.test(value),
			label: 'Contenere almeno una lettera maiuscola',
		},
		number: {
			isValid: (value) => /\d/.test(value),
			label: 'Contenere almeno un numero',
		},
		specialChar: {
			isValid: (value) => /[!@#$%^&*(),.?":{}|<>]/.test(value),
			label: 'Contenere almeno un carattere speciale',
		},
	};

	const [validConditions, setValidConditions] = useState({
		length: false,
		uppercase: false,
		number: false,
		specialChar: false,
	});

	const [hiddenFields, setHiddenFields] = useState({
		oldPassword: true,
		password: true,
		passwordConfirmation: true,
	});

	return (
		<>
			<Form
				autoComplete="off"
				onSubmit={(event) => {
					event.preventDefault();
					handleSavePassword();
				}}
			>
				<p style={{ lineHeight: '28px', margin: '8px 0 48px' }}>
					Per una password sicura, devi usare almeno 10 caratteri, includendo almeno una maiuscola, un numero e un carattere speciale.
				</p>
				<Row style={{ marginBottom: '48px' }}>
					<Form.Group as={Col} sm={3}>
						<Form.Label style={{ fontWeight: 700, lineHeight: '28px' }}>
							<OETrans code="profile.oldPassword" fallback={'Password attuale'} />
							&nbsp;<span className="required-field">*</span>
						</Form.Label>
						<InputGroup>
							<Form.Control
								type={hiddenFields.oldPassword ? 'password' : 'text'}
								name="oldPassword"
								placeholder="Inserisci la tua password attuale"
								value={profile.oldPassword || ''}
								{...(isLoading && { disabled: true })}
								autoComplete="new-password"
								onChange={(e) => handleChange(e)}
								{...((fieldsError.oldPassword || serverErrorFields.oldPassword) && {
									isInvalid: true,
								})}
								style={{ borderRight: 'none' }}
							/>
							<Button
								aria-label={hiddenFields.oldPassword ? 'Mostra password' : 'Nascondi password'}
								onClick={() => {
									setHiddenFields({ ...hiddenFields, oldPassword: !hiddenFields.oldPassword });
								}}
								className="custom-ghost-button"
								style={{
									border: '1px solid rgb(179, 179, 179)',
									borderLeft: 'none',
									borderRadius: '0 8px 8px 0',
									borderColor:
										fieldsError.oldPassword || serverErrorFields.oldPassword ? 'var(--bs-form-invalid-border-color)' : 'rgb(179, 179, 179)',
								}}
							>
								{hiddenFields.oldPassword ? <Eye className="eye" /> : <EyeSlash className="eye" />}
							</Button>
							<Form.Control.Feedback type="invalid">La password inserita non è corretta</Form.Control.Feedback>
						</InputGroup>
					</Form.Group>
				</Row>
				<Row style={{ marginBottom: '48px' }}>
					<Form.Group as={Col} sm={3}>
						<Form.Label style={{ fontWeight: 700, lineHeight: '28px' }}>
							<OETrans code="profile.newPassword" fallback={'Nuova password'} />
							&nbsp;<span className="required-field">*</span>
						</Form.Label>
						<InputGroup>
							<Form.Control
								type={hiddenFields.password ? 'password' : 'text'}
								name="password"
								placeholder="Inserisci la tua nuova password"
								value={profile.password || ''}
								autoComplete="new-password"
								{...(isLoading && { disabled: true })}
								onChange={(e) => {
									handleChange(e);

									setValidConditions(
										Object.fromEntries(Object.entries(validationMap).map(([key, { isValid }]) => [key, isValid(e.target.value)])),
									);
								}}
								{...((fieldsError.password || serverErrorFields.password) && { isInvalid: true })}
								style={{ borderRight: 'none' }}
							/>
							<Button
								aria-label={hiddenFields.oldPassword ? 'Mostra password' : 'Nascondi password'}
								onClick={() => {
									setHiddenFields({ ...hiddenFields, password: !hiddenFields.password });
								}}
								className="custom-ghost-button"
								style={{
									border: '1px solid rgb(179, 179, 179)',
									borderLeft: 'none',
									borderRadius: '0 8px 8px 0',
									borderColor:
										fieldsError.oldPassword || serverErrorFields.oldPassword ? 'var(--bs-form-invalid-border-color)' : 'rgb(179, 179, 179)',
								}}
							>
								{hiddenFields.password ? <Eye className="eye" /> : <EyeSlash className="eye" />}
							</Button>
							<Form.Control.Feedback type="invalid">La password non soddisfa i criteri indicati</Form.Control.Feedback>
						</InputGroup>
					</Form.Group>
					<Form.Group as={Col} sm={3}>
						<Form.Label style={{ fontWeight: 700, lineHeight: '28px' }}>
							<OETrans code="profile.confirmNewPassword" fallback={'Conferma nuova password'} />
							&nbsp;<span className="required-field">*</span>
						</Form.Label>
						<InputGroup>
							<Form.Control
								type={hiddenFields.passwordConfirmation ? 'password' : 'text'}
								name="passwordConfirmation"
								placeholder="Conferma la tua nuova password"
								autoComplete="new-password"
								value={profile.passwordConfirmation || ''}
								{...(isLoading && { disabled: true })}
								onChange={(e) => handleChange(e)}
								{...((fieldsError.passwordConfirmation || serverErrorFields.passwordConfirmation) && {
									isInvalid: true,
								})}
								style={{ borderRight: 'none' }}
							/>
							<Button
								aria-label={hiddenFields.oldPassword ? 'Mostra password' : 'Nascondi password'}
								onClick={() => {
									setHiddenFields({ ...hiddenFields, passwordConfirmation: !hiddenFields.passwordConfirmation });
								}}
								className="custom-ghost-button"
								style={{
									border: '1px solid rgb(179, 179, 179)',
									borderLeft: 'none',
									borderRadius: '0 8px 8px 0',
									borderColor:
										fieldsError.oldPassword || serverErrorFields.oldPassword ? 'var(--bs-form-invalid-border-color)' : 'rgb(179, 179, 179)',
								}}
							>
								{hiddenFields.passwordConfirmation ? <Eye className="eye" /> : <EyeSlash className="eye" />}
							</Button>
							<Form.Control.Feedback type="invalid">La password non coincide</Form.Control.Feedback>
						</InputGroup>
					</Form.Group>
				</Row>
				<Row style={{ marginBottom: '48px' }}>
					<Col
						sm={3}
						style={{
							backgroundColor: '#E3E7EB',
							borderRadius: '8px',
							padding: '24px',
							marginLeft: 'calc(var(--bs-gutter-x) * 0.5)',
						}}
					>
						<p style={{ fontWeight: 700 }}>La password deve:</p>
						<ul style={{ listStyleType: 'none', paddingLeft: 0 }}>
							{Object.entries(validationMap).map(([key, { isValid, label }]) => (
								<li key={key} style={{ marginBottom: '4px' }}>
									{!validConditions[key] ? (
										<span
											style={{
												backgroundColor: '#1C2743',
												borderRadius: '100%',
												width: '16px',
												height: '16px',
												display: 'inline-block',
												marginRight: '8px',
											}}
										/>
									) : (
										<ConfirmSolid
											style={{
												width: '16px',
												height: '16px',
												marginRight: '8px',
											}}
										/>
									)}
									{label}
								</li>
							))}
						</ul>
					</Col>
				</Row>
				<Row style={{ marginBottom: '8px' }}>
					<Col style={{ display: 'flex', justifyContent: 'flex-end', gap: '24px' }}>
						<Button
							type="button"
							className="btn custom-outline-button"
							style={{
								width: '200px',
								height: '48px',
							}}
							onClick={() => {
								callback && callback();
								setProfile({
									...profile,
									oldPassword: '',
									password: '',
									passwordConfirmation: '',
								});
							}}
						>
							Annulla
						</Button>
						<Button
							type="submit"
							disabled={isLoading || Object.keys(fieldsError).length > 0 || !profile.password || !profile.passwordConfirmation}
							className="btn custom-primary-button"
							style={{
								width: '200px',
								height: '48px',
							}}
						>
							Conferma
						</Button>
					</Col>
				</Row>
			</Form>
			<Modal
				show={isSaved}
				onHide={() => {
					setIsSaved(false);
					callback && callback();
				}}
				centered
			>
				<Modal.Header
					style={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						gap: '16px',
					}}
				>
					<ConfirmSolid style={{ margin: '8px' }} />
					<h3
						style={{
							fontSize: '24px',
							textAlign: 'center',
							lineHeight: '35px',
							marginBottom: 0,
							fontWeight: 700,
						}}
					>
						Perfetto!
					</h3>
				</Modal.Header>
				<Modal.Body
					style={{
						padding: '0 16px',
					}}
				>
					<p
						style={{
							textAlign: 'center',
							marginBottom: 0,
							lineHeight: '28px',
						}}
					>
						Password modificata correttamente.
					</p>
				</Modal.Body>
				<Modal.Footer>
					<Button
						className="btn custom-primary-button"
						style={{
							width: '100%',
							height: '48px',
						}}
						onClick={() => {
							setIsSaved(false);
							callback && callback();
						}}
					>
						Chiudi
					</Button>
				</Modal.Footer>
			</Modal>
			<Modal
				show={isError}
				onHide={() => {
					setIsError(false);
				}}
				centered
			>
				<Modal.Header
					style={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						gap: '16px',
					}}
				>
					<RejectSolid style={{ margin: '8px' }} />
					<h3
						style={{
							fontSize: '24px',
							textAlign: 'center',
							lineHeight: '35px',
							marginBottom: 0,
							fontWeight: 700,
						}}
					>
						Qualcosa è andato storto!
					</h3>
				</Modal.Header>
				<Modal.Body
					style={{
						padding: '0 16px',
					}}
				>
					<p
						style={{
							textAlign: 'center',
							marginBottom: 0,
							lineHeight: '28px',
						}}
					>
						Non è stato possibile modificare la password.
					</p>
				</Modal.Body>
				<Modal.Footer>
					<Button
						className="btn custom-outline-button"
						style={{
							width: '100%',
							height: '48px',
						}}
						onClick={() => {
							setIsError(false);
						}}
					>
						Chiudi
					</Button>
					<p style={{ width: '100%', textAlign: 'center', margin: '1rem 0' }}>Oppure</p>
					<Button
						onClick={() => {
							setIsError(false);
							handleSavePassword();
						}}
						className="btn custom-primary-button"
						style={{
							width: '100%',
							height: '48px',
						}}
					>
						Riprova
					</Button>
				</Modal.Footer>
			</Modal>
		</>
	);
}
